import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2 } from 'lucide-react';
// CR-5369: LoginAs (Impersonation) section removed from this tab — the
// feature now lives only under User Management. Imports + props for the
// removed section dropped to keep the surface clean.
import api from '../../lib/api/client';
import { TokenManager } from '../../lib/token-manager';
import { localRequirementsMet, passwordRequirementItems } from '../../lib/passwordPolicy';
import { PasswordRequirements } from '../../components/auth/PasswordRequirements';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

export function SecurityTab() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  // CR-8598: a refused new password stays on the form until it is edited,
  // not in a toast that disappears (a breach is only known on save).
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);

  // CR-8598: the platform policy, ticked as the user types. The breach check
  // needs the server, so that row is answered on save.
  const requirementItems = passwordRequirementItems(newPassword, {
    confirm: confirmPassword,
    owner: user ? {
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      companyNames: [user.companyName],
    } : undefined,
  });

  const handleChangePassword = async () => {
    setNewPasswordError(null);
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (!localRequirementsMet(requirementItems)) {
      toast.error('New password does not meet the password requirements');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/auth/change-password/', {
        old_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });

      // CR-7442: the server deletes the old token and returns the new one in
      // the standard envelope ({ status, data: { token } }). Reading
      // response.data.token found nothing, the SPA kept the deleted token and
      // the next request 401'd to /login. Store it the way login does.
      const newToken = response.data?.data?.token ?? response.data?.token;
      if (typeof newToken === 'string' && newToken) {
        TokenManager.setToken(newToken);
      }

      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const error = err as Record<string, unknown>;
      if (error['response']) {
        const resp = error['response'] as Record<string, unknown>;
        const data = resp['data'] as Record<string, unknown> | undefined;
        const details = data?.['details'] as Record<string, unknown> | undefined;
        const fields = details?.['fields'] as Record<string, string[]> | undefined;

        if (fields?.['old_password']) {
          toast.error(fields['old_password'][0] || 'Current password is incorrect');
        } else if (fields?.['new_password']) {
          setNewPasswordError(fields['new_password'][0] || 'Invalid new password');
        } else if (fields?.['new_password_confirm']) {
          toast.error(fields['new_password_confirm'][0] || 'Passwords do not match');
        } else if (data?.['message']) {
          toast.error(String(data['message']));
        } else {
          toast.error('Failed to update password');
        }
      } else {
        toast.error('Failed to update password');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            Update your password to keep your account secure.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="space-y-4"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              handleChangePassword();
            }}
          >
            <PasswordRequirements
              id="password-requirements"
              items={requirementItems}
              note="The data-breach check runs when you save."
            />
            {newPasswordError && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">{newPasswordError}</p>
            )}
            {/* Hidden username field so the browser's password manager has an explicit
                target and doesn't scavenge the nearest text input (e.g. the global
                search bar) as the username. */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={user?.email || ''}
              readOnly
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                aria-describedby="password-requirements"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setNewPasswordError(null); }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* CR-5369: Login As (Impersonation) section removed — duplicated
          User Management functionality. Impersonation remains accessible
          via User Management for admins; removing it from personal Settings
          reduces misuse risk. */}
    </div>
  );
}
